"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Walk the tree for `SelectItem`s and build the value → label map Base UI
 * needs.
 *
 * Without a Root `items` map, `<Select.Value>` renders the raw *value*
 * ("hiive") instead of the item's label ("Whole hiive"). Deriving it here
 * means call sites keep the plain `<SelectValue />` form and still read
 * correctly — the same trap a native `<select>` never had.
 *
 * Only plain string/number item children can be mapped; anything richer is
 * left to the caller's own `items` prop.
 *
 * **A component boundary is opaque to this walk.** Items rendered by a wrapper
 * of your own (`<MyGroup items={...} />`) do not exist until that component
 * renders, so nothing is found, no map is built, and the trigger silently shows
 * the raw value again. Pass `items` explicitly whenever the SelectItems are not
 * written literally inside the Select.
 */
function deriveItemLabels(
  node: React.ReactNode,
  out: Record<string, React.ReactNode>,
) {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as {
      value?: unknown;
      children?: React.ReactNode;
    };
    // Reference equality, not displayName — SelectItem is a plain function
    // declaration (hoisted), so this is exact and rename-safe.
    if (child.type === SelectItem && props.value != null) {
      const label = props.children;
      if (typeof label === "string" || typeof label === "number") {
        out[String(props.value)] = label;
      }
    }
    if (props.children) deriveItemLabels(props.children, out);
  });
}

type SelectValue<
  Value,
  Multiple extends boolean | undefined,
> = Multiple extends true ? Value[] : Value;

type SelectProps<Value, Multiple extends boolean | undefined = false> = Omit<
  SelectPrimitive.Root.Props<Value, Multiple>,
  "onValueChange"
> & {
  onValueChange?: (
    value: SelectValue<Value, Multiple>,
    eventDetails: SelectPrimitive.Root.ChangeEventDetails,
  ) => void;
};

function Select<Value, Multiple extends boolean | undefined = false>({
  onValueChange,
  items,
  children,
  ...props
}: SelectProps<Value, Multiple>) {
  // An explicit `items` prop always wins; otherwise derive it from the
  // rendered SelectItems so <SelectValue /> shows labels, not raw values.
  const derivedItems = React.useMemo(() => {
    if (items) return items;
    const map: Record<string, React.ReactNode> = {};
    deriveItemLabels(children, map);
    return Object.keys(map).length > 0 ? map : undefined;
  }, [items, children]);

  return (
    <SelectPrimitive.Root
      {...props}
      items={
        derivedItems as SelectPrimitive.Root.Props<Value, Multiple>["items"]
      }
      onValueChange={(value, eventDetails) => {
        if (value !== null) {
          onValueChange?.(value as SelectValue<Value, Multiple>, eventDetails);
        }
      }}
    >
      {children}
    </SelectPrimitive.Root>
  );
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex h-10 w-fit items-center justify-between gap-2 rounded-xl border border-input bg-background/80 px-3 py-2 text-sm whitespace-nowrap text-foreground shadow-xs transition-[color,box-shadow,border-color,background] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:ring-destructive/40 data-disabled:cursor-not-allowed data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        className="text-muted-foreground"
        render={<ChevronDownIcon className="size-4 opacity-50" />}
      />
    </SelectPrimitive.Trigger>
  );
}

type SelectContentProps = React.ComponentProps<typeof SelectPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof SelectPrimitive.Positioner>,
    "align" | "alignItemWithTrigger" | "alignOffset" | "side" | "sideOffset"
  > & {
    /**
     * Classes for the Positioner, which is what carries the stacking layer.
     * `className` lands on the popup *inside* an `isolation: isolate` wrapper,
     * so a `z-*` there can never lift the dropdown above a sibling overlay —
     * use this to override `--z-popup` for one call site.
     */
    positionerClassName?: string;
  };

function SelectContent({
  align,
  alignItemWithTrigger = false,
  alignOffset,
  children,
  className,
  positionerClassName,
  side,
  sideOffset = 4,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        align={align}
        alignItemWithTrigger={alignItemWithTrigger}
        alignOffset={alignOffset}
        // Portaled into <body>, which an open vaul Drawer sets to
        // `pointer-events: none`; without this every tap on an option falls
        // through to whatever sits behind the popup.
        className={cn(
          "pointer-events-auto isolate z-(--z-popup)",
          positionerClassName,
        )}
        side={side}
        sideOffset={sideOffset}
      >
        <SelectPrimitive.Popup
          className={cn(
            "max-h-[var(--available-height)] min-w-[8rem] origin-[var(--transform-origin)] overflow-hidden rounded-2xl border border-border bg-popover/95 text-popover-foreground opacity-100 shadow-xl backdrop-blur-xl transition-[opacity,transform] duration-200 ease-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 dark:shadow-2xl",
            className,
          )}
          data-slot="select-content"
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="w-full min-w-[var(--anchor-width)] scroll-my-1 p-1.5">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) {
  return (
    <SelectPrimitive.GroupLabel
      className={cn(
        "px-2 py-1.5 text-xs font-medium text-muted-foreground",
        className,
      )}
      data-slot="select-label"
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-xl py-2 pr-8 pl-2 text-sm outline-hidden transition-colors select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="select-item"
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4 stroke-[2.5]" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      data-slot="select-separator"
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      data-slot="select-scroll-up-button"
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      data-slot="select-scroll-down-button"
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
